//! No found external controls

const CarouselAlign = ['start', 'center', 'end'];

/**
 * @class NumCarousel
 * @extends HTMLElement
 * @description A custom web component for a sliding carousel with configurable properties.
 * @attribute {string} align - The alignment of the carousel ('start', 'center', 'end').
 * @attribute {boolean} loop - Indicates whether the carousel should loop.
 * @attribute {boolean} autoplay - Indicates whether the carousel should autoplay.
 * @attribute {boolean} contain-scroll - Indicates whether the carousel should contain scroll.
 * @attribute {string} slide-width - The width of each slide.
 * @attribute {string} slide-height - The height of each slide.
 * @attribute {string} slide-gap - The gap between slides.
 * @attribute {boolean} with-controls - Indicates whether the carousel should have controls.
 * @attribute {boolean} with-dots - Indicates whether the carousel should have dots.
 * @attribute {boolean} with-thumbs - Indicates whether the carousel should have thumbs.
 * @attribute {boolean} with-class-names - Indicates whether the carousel should use class names.
 */
class NumCarousel extends HTMLElement {
  static observedAttributes = [
    'align',
    'loop',
    'autoplay',
    'contain-scroll',
    'slide-width',
    'slide-height',
    'slide-gap',
    'with-controls',
    'with-dots',
    'with-thumbs',
    'with-class-names',
  ];

  constructor() {
    super();
    this._id = `NumCarousel-${Math.random().toString(36).slice(2, 11)}`;
    this._embla;
    this._props = {
      align: 'start',
      loop: false,
      autoplay: false,
      containScroll: false,
      slideWidth: '100%',
      slideHeight: '200px',
      transitionDuration: 1500,
      slideGap: '0',
      withControls: false,
      withDots: false,
      withThumbs: false,
      withClassNames: false,
    };
  }

  connectedCallback() {
    this._render();
    this._updateCarouselState();
    this._init();
  }

  attributeChangedCallback() {
    this._updateCarouselState();
  }

  get getEmbla() {
    return this._embla;
  }

  _updateProps() {
    this._props = {
      align: this.getAttribute('align') || this._props.align,
      loop: this.hasAttribute('loop'),
      autoplay: this.hasAttribute('autoplay'),
      containScroll: this.hasAttribute('contain-scroll'),
      slideWidth: this.getAttribute('slide-width') || this._props.slideWidth,
      slideHeight: this.getAttribute('slide-height') || this._props.slideHeight,
      slideGap: this.getAttribute('slide-gap') || this._props.slideGap,
      withControls: this.hasAttribute('with-controls'),
      withDots: this.hasAttribute('with-dots'),
      withThumbs: this.hasAttribute('with-thumbs'),
      withClassNames: this.hasAttribute('with-class-names'),
    };
    this.id = this._props.withControls || this._props.withDots ? this._id : this.getAttribute('id');
  }

  _updateCarouselState() {
    this._updateProps();

    this.style.setProperty('--num-slide-width', this._props.slideWidth);
    this.style.setProperty('--num-slide-height', this._props.slideHeight);
    this.style.setProperty('--num-slide-gap', this._props.slideGap);
  }

  _render() {
    this.innerHTML = `
      <div class="num-carousel__viewport" id=${this._id}>
        <div class="num-carousel__container">
          ${Array.from(this.children)
            .map((child) => {
              return `<div class="num-carousel__slide">${child.outerHTML}</div>`;
            })
            .join('')}
        </div>
      </div>
      ${
        this._props.withControls
          ? `<num-carousel-controls for="${this._id}" class="num-carousel__controls">
            <button slot="prev-button" class="num-carousel__btn num-carousel__prev-btn">
              <svg width="11" height="19" viewBox="0 0 11 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M1.72821 9.50023L10.3433 1.36373L9.65669 0.636719L0.271805 9.50023L9.65669 18.3637L10.3433 17.6367L1.72821 9.50023Z"/>
              </svg>
            </button>
            <button slot="next-button" class="num-carousel__btn num-carousel__next-btn">
              <svg width="11" height="19" viewBox="0 0 11 19" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path fill-rule="evenodd" clip-rule="evenodd" d="M9.46369 9.69167L0.848578 17.8282L1.5352 18.5552L10.9201 9.69167L1.5352 0.828162L0.848579 1.55517L9.46369 9.69167Z"/>
              </svg>
            </button>
          </num-carousel-controls>`
          : ''
      }
      ${
        this._props.withDots
          ? `<num-carousel-dots for="${this._id}" class="num-carousel__dots"></num-carousel-dots>`
          : ''
      }
      ${
        this._props.withThumbs
          ? `<num-carousel-thumbs for-main="${this._id}" for-thumb="${this._id}-thumbs" class="num-carousel__thumbs"></num-carousel-thumbs>`
          : ''
      }
    `;
  }

  _getPlugins() {
    const plugins = [];
    if (this._props.withClassNames) plugins.push(EmblaCarouselClassNames());
    if (this._props.autoplay) plugins.push(EmblaCarouselAutoplay());
    return plugins;
  }

  _getOptions() {
    const align = CarouselAlign.includes(this._props.align)
      ? this._props.align
      : (viewSize, snapSize, index) => {
          const alignPercentage = parseFloat(this._props.align);
          return !isNaN(alignPercentage) ? viewSize * (alignPercentage / 100) : viewSize * 0.5;
        };

    return {
      align,
      loop: this._props.loop,
      containScroll: this._props.containScroll,
    };
  }

  _init() {
    const OPTIONS = this._getOptions();
    const PLUGINS = this._getPlugins();
    const viewportNode = this.querySelector('.num-carousel__viewport');

    this._embla = EmblaCarousel(viewportNode, OPTIONS, PLUGINS);
    console.log(this._embla.slideNodes());

    if (this._props.withControls) {
      const controls = this.querySelector('num-carousel-controls');
      console.log(controls);
      controls.setEmbla = this._embla;
    }

    if (this._props.withDots) {
      const dots = this.querySelector('num-carousel-dots');
      console.log(dots);
      dots.setEmbla = this._embla;
    }

    if (this._props.withThumbs) {
      const thumbs = this.querySelector('num-carousel-thumbs');
      console.log(thumbs);
      thumbs.setEmbla = this._embla;
    }
  }
}

customElements.define('num-carousel', NumCarousel);

/**
 * @class NumCarouselControls
 * @extends HTMLElement
 *
 * @description A custom web component for carousel controls.
 * @attribute {string} for - The ID of the carousel to control.
 */

class NumCarouselControls extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._embla = null;
  }

  connectedCallback() {
    this._render();
    this._initEmblaInstance();
    this._init();
  }

  addTogglePrevNextBtnsActive(emblaApi, prevBtn, nextBtn) {
    const togglePrevNextBtnsState = () => {
      prevBtn.toggleAttribute('disabled', !emblaApi.canScrollPrev());
      nextBtn.toggleAttribute('disabled', !emblaApi.canScrollNext());
    };

    emblaApi
      .on('select', togglePrevNextBtnsState)
      .on('init', togglePrevNextBtnsState)
      .on('reInit', togglePrevNextBtnsState);

    return () => {
      prevBtn.removeAttribute('disabled');
      nextBtn.removeAttribute('disabled');
    };
  }

  addPrevNextBtnsClickHandlers(emblaApi, prevBtn, nextBtn) {
    const scrollPrev = () => emblaApi.scrollPrev();
    const scrollNext = () => emblaApi.scrollNext();

    if (null === prevBtn || null === nextBtn) {
      return;
    }
    prevBtn.addEventListener('click', scrollPrev, false);
    nextBtn.addEventListener('click', scrollNext, false);

    const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(emblaApi, prevBtn, nextBtn);

    return () => {
      removeTogglePrevNextBtnsActive();
      prevBtn.removeEventListener('click', scrollPrev, false);
      nextBtn.removeEventListener('click', scrollNext, false);
    };
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <slot name="prev-button"></slot>
      <slot name="next-button"></slot>
    `;
  }

  _initEmblaInstance() {
    const carouselId = this.getAttribute('for');
    const carousel = document.querySelector(`#${carouselId}`);
    if (carousel) {
      this._embla = carousel.getEmbla;
    }
  }

  _init() {
    const prevButton = this.shadowRoot.querySelector('slot[name="prev-button"]').assignedElements()[0];
    const nextButton = this.shadowRoot.querySelector('slot[name="next-button"]').assignedElements()[0];

    prevButton.addEventListener('click', () => console.log('prev button clicked'));
    nextButton.addEventListener('click', () => console.log('next button clicked'));

    console.log(this._embla);

    if (this._embla) {
      console.log('embla instance found');
      this.addPrevNextBtnsClickHandlers(this._embla, prevButton, nextButton);
    }
  }
}

customElements.define('num-carousel-controls', NumCarouselControls);

/**
 * @class NumCarouselDots
 * @extends HTMLElement
 *
 * @description A custom web component for carousel dots.
 * @attribute {string} for - The ID of the carousel to control.
 */

class NumCarouselDots extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._embla = null;
  }

  connectedCallback() {
    this._render();
    this._initEmblaInstance();
    this._init();
  }

  addDotBtnsAndClickHandlers(emblaApi, dotsNode) {
    let dotNodes = [];

    const addDotBtnsWithClickHandlers = () => {
      dotsNode.innerHTML = emblaApi
        .scrollSnapList()
        .map(() => '<button class="embla__dot" type="button"></button>')
        .join('');

      const scrollTo = (index) => {
        emblaApi.scrollTo(index);
      };

      dotNodes = Array.from(dotsNode.querySelectorAll('.embla__dot'));
      dotNodes.forEach((dotNode, index) => {
        dotNode.addEventListener('click', () => scrollTo(index), false);
      });
    };

    const toggleDotBtnsActive = () => {
      const previous = emblaApi.previousScrollSnap();
      const selected = emblaApi.selectedScrollSnap();
      dotNodes[previous].classList.remove('embla__dot--selected');
      dotNodes[selected].classList.add('embla__dot--selected');
    };

    emblaApi
      .on('init', addDotBtnsWithClickHandlers)
      .on('reInit', addDotBtnsWithClickHandlers)
      .on('init', toggleDotBtnsActive)
      .on('reInit', toggleDotBtnsActive)
      .on('select', toggleDotBtnsActive);

    return () => {
      dotsNode.innerHTML = '';
    };
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <style>${this._getStyles()}</style>
      <div class="embla__dots"></div>
    `;
  }

  _getStyles() {
    return `
      .embla__dots {
        display: flex;
        gap: 8px;
        justify-content: center;
        margin-top: 16px;
      }
      .embla__dot {
        width: 35px;
        height: 2px;
        cursor: pointer;
        border: none;
        padding: 10px 0;
        display: flex;
        position: relative;
        background-color: transparent;
      }

      .embla__dot::before {
        content: "";
        width: 100%;
        height: 4px;
        position: absolute;
        background-color: #999;
      }

      .embla__dot--selected::before {
        background-color: #ffff;
      }
    `;
  }

  _initEmblaInstance() {
    const carouselId = this.getAttribute('for');
    const carousel = document.querySelector(`#${carouselId}`);
    if (carousel) {
      this._embla = carousel.getEmbla;
    }
  }

  _init() {
    const dotsNode = this.shadowRoot.querySelector('.embla__dots');

    if (this._embla) {
      this.addDotBtnsAndClickHandlers(this._embla, dotsNode);
    }
  }
}

customElements.define('num-carousel-dots', NumCarouselDots);

class NumCarouselThumbs extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._emblaMain = null;
    this._emblaThumb = null;
  }

  connectedCallback() {
    this._render();
    this._initEmblaInstances();
    this._init();
  }

  addThumbBtnsClickHandlers(emblaApiMain, emblaApiThumb) {
    const slidesThumbs = emblaApiThumb.slideNodes();

    const scrollToIndex = slidesThumbs.map((_, index) => () => emblaApiMain.scrollTo(index));

    slidesThumbs.forEach((slideNode, index) => {
      slideNode.addEventListener('click', scrollToIndex[index], false);
    });

    return () => {
      slidesThumbs.forEach((slideNode, index) => {
        slideNode.removeEventListener('click', scrollToIndex[index], false);
      });
    };
  }

  addToggleThumbBtnsActive(emblaApiMain, emblaApiThumb) {
    const slidesThumbs = emblaApiThumb.slideNodes();

    const toggleThumbBtnsState = () => {
      emblaApiThumb.scrollTo(emblaApiMain.selectedScrollSnap());
      const previous = emblaApiMain.previousScrollSnap();
      const selected = emblaApiMain.selectedScrollSnap();
      slidesThumbs[previous].classList.remove('embla-thumbs__slide--selected');
      slidesThumbs[selected].classList.add('embla-thumbs__slide--selected');
    };

    emblaApiMain.on('select', toggleThumbBtnsState);
    emblaApiThumb.on('init', toggleThumbBtnsState);

    return () => {
      const selected = emblaApiMain.selectedScrollSnap();
      slidesThumbs[selected].classList.remove('embla-thumbs__slide--selected');
    };
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <style>${this._getStyles()}</style>
      <div class="embla-thumbs__viewport">
        <div class="embla-thumbs__container">
          <slot></slot>
        </div>
      </div>
    `;
  }

  _getStyles() {
    return `
      :host {
        display: block;
        margin-top: 16px;
      }
      .embla-thumbs__viewport {
        overflow: hidden;
        width: 100%;
      }
      .embla-thumbs__container {
        display: flex;
      }
      .embla-thumbs__slide {
        flex: 0 0 auto;
        cursor: pointer;
      }
      .embla-thumbs__slide--selected {
        border: 2px solid #000;
      }
    `;
  }

  _initEmblaInstances() {
    const mainCarouselId = this.getAttribute('for-main');
    const thumbCarouselId = this.getAttribute('for-thumb');
    const mainCarousel = document.querySelector(`#${mainCarouselId}`);
    const thumbCarousel = document.querySelector(`#${thumbCarouselId}`);

    if (mainCarousel) {
      this._emblaMain = mainCarousel.getEmbla;
    }
    if (thumbCarousel) {
      this._emblaThumb = thumbCarousel.getEmbla;
    }
  }

  _init() {
    if (this._emblaMain && this._emblaThumb) {
      this.addThumbBtnsClickHandlers(this._emblaMain, this._emblaThumb);
      this.addToggleThumbBtnsActive(this._emblaMain, this._emblaThumb);
    }
  }
}

customElements.define('num-carousel-thumbs', NumCarouselThumbs);
